// src/modules/app-views/serviceOrders/pages/Document/tabs/ItemsTab/ItemModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Segmented, Input, Row, Col, Button } from 'antd';
import { ToolOutlined, ShoppingOutlined } from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ItemModal = ({ open, onClose, onSave, editingItem }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!editingItem;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [itemType, setItemType] = useState('service');
  const [lineTotal, setLineTotal] = useState(0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (editingItem) {
        form.setFieldsValue({
          ...editingItem,
          quantity: editingItem.quantity || 1,
          unitPrice: editingItem.unitPrice || 0,
          discountValue: editingItem.discountValue || 0,
        });
        setItemType(editingItem.itemType || 'service');
        recalculateTotal(editingItem);
      } else {
        form.resetFields();
        form.setFieldsValue({ quantity: 1, unitPrice: 0, discountValue: 0 });
        setItemType('service');
        setLineTotal(0);
      }
    }
  }, [open, editingItem, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const quantity = Number(values.quantity) || 1;
      const unitPrice = Number(values.unitPrice) || 0;
      const discountValue = Number(values.discountValue) || 0;
      const total = quantity * unitPrice - discountValue;

      onSave({
        ...values,
        itemType,
        quantity,
        unitPrice,
        discountValue,
        lineTotal: Math.round(total * 100) / 100,
      });
    } catch (err) {
      // Form validation failed
    }
  }, [form, itemType, onSave]);

  const handleValuesChange = useCallback(
    (_, allValues) => {
      recalculateTotal(allValues);
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={isEditing ? translateX('edit') : translateX('add_item')}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={translateX('save')}
      cancelText={translateX('cancel')}
      width={600}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        {/* Item Type */}
        <Form.Item label={translateX('item_type')}>
          <Segmented
            options={[
              {
                label: (
                  <span>
                    <ToolOutlined style={{ marginRight: 4 }} />
                    {translateX('service')}
                  </span>
                ),
                value: 'service',
              },
              {
                label: (
                  <span>
                    <ShoppingOutlined style={{ marginRight: 4 }} />
                    {translateX('product')}
                  </span>
                ),
                value: 'product',
              },
            ]}
            value={itemType}
            onChange={setItemType}
            block
          />
        </Form.Item>

        {/* Name and Code */}
        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={16}>
            <Form.Item
              name="name"
              label={translateX('name')}
              rules={[{ required: true, message: () => translateX('required_field') }]}
            >
              <Input placeholder={translateX('name')} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item name="code" label={translateX('code')}>
              <Input placeholder={translateX('code')} />
            </Form.Item>
          </Col>
        </Row>

        {/* Quantity, Price, Discount */}
        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={6}>
            <Form.Item
              name="quantity"
              label={translateX('quantity')}
              rules={[{ required: true, message: () => translateX('required_field') }]}
            >
              <Input type="number" min={1} step={1} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              name="unitPrice"
              label={translateX('unit_price')}
              rules={[{ required: true, message: () => translateX('required_field') }]}
            >
              <Input type="number" min={0} step={0.01} prefix="R$" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={5}>
            <Form.Item name="discountValue" label={translateX('discount')}>
              <Input type="number" min={0} step={0.01} prefix="R$" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={5}>
            <Form.Item label={translateX('subtotal')}>
              <Input
                value={formatCurrency(lineTotal)}
                disabled
                style={{ fontWeight: 'bold' }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Description */}
        <Form.Item name="description" label={translateX('description')}>
          <Input.TextArea rows={2} placeholder={translateX('description')} />
        </Form.Item>
      </Form>
    </Modal>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // INTERNAL HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  function recalculateTotal(values) {
    const qty = Number(values?.quantity) || 1;
    const price = Number(values?.unitPrice) || 0;
    const discount = Number(values?.discountValue) || 0;
    setLineTotal(Math.round((qty * price - discount) * 100) / 100);
  }
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ItemModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Format number as BRL currency string
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}
